import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Trash2, Plus, LogOut, Mail, Building2, CalendarClock, Clock3, Bell, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { GuestDarkToggle, useGuestDarkMode } from "@/hooks/useGuestDarkMode";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const TOKEN_KEY = "ns_wishlist_token";

function guestClient() {
  const t = localStorage.getItem(TOKEN_KEY);
  return axios.create({
    baseURL: API,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
}

function useQueryToken() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get("token"), [search]);
}

export default function GuestWishlist() {
  // Session Z81/Z82 — signed-cookie dark-mode preference, same primitive as GuestBook.
  useGuestDarkMode();
  const urlToken = useQueryToken();
  const nav = useNavigate();
  const verifiedRef = useRef(null);
  const [phase, setPhase] = useState("boot"); // boot | request | signed_in
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState(null);
  const [entries, setEntries] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [frequency, setFrequency] = useState("instant");
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState("");
  const [minNights, setMinNights] = useState(3);
  const [horizonDays, setHorizonDays] = useState(90);

  useEffect(() => {
    if (!urlToken) {
      const stored = localStorage.getItem(TOKEN_KEY);
      setPhase(stored ? "signed_in" : "request");
      return;
    }
    if (verifiedRef.current === urlToken) return;
    verifiedRef.current = urlToken;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/guest/wishlist/verify`, { token: urlToken });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setGuest(data.guest);
        setPhase("signed_in");
        toast.success(`Welcome back${data.guest.first_name ? ", " + data.guest.first_name : ""}`);
        nav("/wishlist", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Link invalid or expired");
        setPhase("request");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  const loadWishlist = async () => {
    const api = guestClient();
    try {
      const [w, p, a, pf] = await Promise.all([
        api.get("/guest/wishlist"),
        axios.get(`${API}/public/wishlist-properties`),
        api.get("/guest/wishlist/alerts"),
        api.get("/guest/wishlist/preferences"),
      ]);
      setEntries(w.data);
      setProperties(p.data);
      setAlerts(a.data);
      setFrequency(pf.data.alert_frequency || "instant");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setPhase("request");
        toast.error("Session expired — please request a fresh link");
      }
    }
  };

  const updateFrequency = async (next) => {
    setFrequency(next);
    const api = guestClient();
    try {
      await api.put("/guest/wishlist/preferences", { alert_frequency: next });
      toast.success(next === "weekly" ? "Switched to weekly digest" : "Switched to instant alerts");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save preference");
    }
  };

  useEffect(() => {
    if (phase !== "signed_in") return;
    loadWishlist();
  }, [phase]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await axios.post(`${API}/guest/wishlist/request-link`, { email });
      setSentTo(email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!selectedProp) return toast.error("Pick a unit first");
    const api = guestClient();
    try {
      await api.post("/guest/wishlist/entry", {
        property_id: selectedProp,
        min_nights: Number(minNights),
        notify_within_days: Number(horizonDays),
      });
      setSelectedProp("");
      toast.success("Added to your wishlist");
      loadWishlist();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this unit from your wishlist?")) return;
    const api = guestClient();
    try {
      await api.delete(`/guest/wishlist/entry/${id}`);
      toast.success("Removed");
      loadWishlist();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setGuest(null);
    setEntries([]);
    setPhase("request");
    setSentTo("");
    setEmail("");
  };

  const availableProperties = properties.filter(
    (p) => !entries.some((e) => e.property_id === p.id),
  );

  if (phase === "boot") {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 text-slate-500">
        Loading…
      </div>
    );
  }

  if (phase === "request") {
    return (
      <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
        <div className="hidden md:block bg-gradient-to-br from-teal-800 to-slate-900 relative">
          <div className="absolute inset-0 flex flex-col justify-between p-14 text-white">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Newport Stays" className="w-14 h-14 rounded-2xl" />
              <div>
                <div className="font-serif text-2xl">Newport Stays</div>
                <div className="ns-label text-teal-200/70 tracking-[0.22em]">Wishlist</div>
              </div>
            </div>
            <div>
              <div className="ns-label text-teal-200/60 mb-4">Pin your favourite home</div>
              <div className="font-serif text-4xl leading-tight">
                We'll ping you the moment<br />it opens up again.
              </div>
            </div>
            <div className="text-xs text-teal-100/50">&copy; 2026 Newport Stays</div>
          </div>
        </div>
        <div className="p-8 md:p-16 grid place-items-center">
          <div className="w-full max-w-md" data-testid="wishlist-request-phase">
            <div className="ns-label text-slate-400">Wishlist Sign In</div>
            <h1 className="font-serif text-4xl mt-2">Watch a favourite unit</h1>
            <p className="text-slate-500 text-sm mt-4">
              Enter the email you booked with — we'll send you a private link so you can add or remove units from your wishlist. No password needed.
            </p>
            {sentTo ? (
              <div
                data-testid="wishlist-link-sent"
                className="mt-8 rounded-2xl border border-teal-100 bg-teal-50/50 p-6 text-sm text-teal-800"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4" /> <span className="font-medium">Check your inbox</span>
                </div>
                We just emailed <strong>{sentTo}</strong> a link. It's good for the next 24 hours.
              </div>
            ) : (
              <form onSubmit={requestLink} className="mt-8 space-y-4">
                <div>
                  <Label className="ns-label">Email</Label>
                  <Input
                    data-testid="wishlist-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 h-12 bg-white"
                  />
                </div>
                <Button
                  data-testid="wishlist-request-btn"
                  disabled={busy}
                  type="submit"
                  className="w-full rounded-full ns-btn-primary h-12"
                >
                  {busy ? "Sending…" : "Email me a link"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Signed in
  return (
    <div className="min-h-screen bg-stone-50" data-testid="wishlist-signed-in">
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Newport Stays" className="w-9 h-9 rounded-xl" />
            <div>
              <div className="ns-label text-slate-400">Newport Stays Wishlist</div>
              <div className="text-sm font-medium">
                {guest?.first_name ? `${guest.first_name} · ` : ""}
                {guest?.email}
              </div>
            </div>
          </div>
          <Button data-testid="wishlist-signout" variant="ghost" onClick={signOut} className="rounded-full text-slate-500">
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
          <GuestDarkToggle />
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 md:p-10">
        <div className="ns-card p-5" data-testid="wishlist-frequency-card">
          <div className="flex items-center gap-3">
            <MailPlus className="w-5 h-5 text-teal-700 shrink-0" />
            <div className="flex-1">
              <div className="ns-label">Alert frequency</div>
              <div className="text-xs text-slate-400 mt-1">
                Instant emails the moment we find a window, or one calm summary each week.
              </div>
            </div>
            <div className="flex rounded-full bg-stone-100 p-1 text-xs">
              <button
                data-testid="wishlist-freq-instant"
                onClick={() => updateFrequency("instant")}
                className={`px-4 py-1.5 rounded-full transition ${
                  frequency === "instant"
                    ? "bg-white text-teal-800 shadow-sm font-medium"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Instant
              </button>
              <button
                data-testid="wishlist-freq-weekly"
                onClick={() => updateFrequency("weekly")}
                className={`px-4 py-1.5 rounded-full transition ${
                  frequency === "weekly"
                    ? "bg-white text-teal-800 shadow-sm font-medium"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Weekly digest
              </button>
            </div>
          </div>
        </div>

        <div className="ns-card p-6 md:p-8 mt-6">
          <div className="flex items-center gap-3">
            <Heart className="w-5 h-5 text-teal-700" />
            <div>
              <div className="ns-label">Add a unit to watch</div>
              <div className="text-xs text-slate-400 mt-1">
                We'll email you the moment an open window matching your minimum stay length appears.
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-[1fr_140px_140px_auto] gap-3 mt-5">
            <div>
              <Label className="ns-label">Unit</Label>
              <Select value={selectedProp} onValueChange={setSelectedProp}>
                <SelectTrigger data-testid="wishlist-property-select" className="mt-2 h-11 bg-white">
                  <SelectValue placeholder={availableProperties.length ? "Select a unit" : "Every unit already on your list"} />
                </SelectTrigger>
                <SelectContent>
                  {availableProperties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="ns-label">Min. nights</Label>
              <Input
                data-testid="wishlist-min-nights"
                type="number"
                min={1}
                max={30}
                value={minNights}
                onChange={(e) => setMinNights(e.target.value)}
                className="mt-2 h-11 bg-white"
              />
            </div>
            <div>
              <Label className="ns-label">Within (days)</Label>
              <Input
                data-testid="wishlist-horizon"
                type="number"
                min={7}
                max={365}
                value={horizonDays}
                onChange={(e) => setHorizonDays(e.target.value)}
                className="mt-2 h-11 bg-white"
              />
            </div>
            <div className="self-end">
              <Button
                data-testid="wishlist-add-btn"
                onClick={add}
                disabled={!selectedProp}
                className="rounded-full ns-btn-primary h-11 px-6"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </div>
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="mt-8 ns-card p-5" data-testid="wishlist-alerts-panel">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4 text-amber-600" />
              <div className="ns-label">Recent alerts</div>
              <span className="text-[11px] text-slate-400 ml-auto">
                Last 5 windows we told you about
              </span>
            </div>
            <div className="space-y-2">
              {alerts.map((a) => {
                const bookUrl = `/book?property_id=${a.property_id}&check_in=${a.gap_start}&check_out=${a.gap_end}`;
                return (
                  <a
                    key={a.id}
                    data-testid={`wishlist-alert-${a.id}`}
                    href={bookUrl}
                    className="flex items-center gap-3 rounded-lg bg-amber-50/60 border border-amber-100 px-4 py-3 hover:bg-amber-50 transition"
                  >
                    <Bell className="w-4 h-4 text-amber-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.property_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {a.gap_start} → {a.gap_end} · {a.min_nights} nights
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-400 shrink-0">
                      {new Date(a.sent_at).toLocaleDateString()}
                    </div>
                    <span className="text-xs text-amber-700 font-medium shrink-0">Re-book →</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="ns-label px-1 mb-3">Your wishlist ({entries.length})</div>
          {entries.length === 0 ? (
            <div className="ns-card p-10 text-center text-slate-400 italic text-sm">
              No units on your wishlist yet. Add one above and we'll watch it for you.
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((e) => (
                <div
                  key={e.id}
                  data-testid={`wishlist-entry-${e.id}`}
                  className="ns-card p-5 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">
                    <Building2 className="w-5 h-5 text-teal-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.property_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1">
                        <Clock3 className="w-3 h-3" /> {e.min_nights}+ nights
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" /> Next {e.notify_within_days} days
                      </span>
                      {e.last_notified_at && (
                        <span className="text-teal-700">
                          Alerted {new Date(e.last_notified_at).toLocaleDateString()}
                          {e.last_gap_start && ` · ${e.last_gap_start} → ${e.last_gap_end}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`wishlist-remove-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="text-slate-400 hover:text-red-600 transition"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
